import { useCallback, useRef } from 'react';
import {
  ColumnPinnedEvent,
  ColumnVisibleEvent,
  DragStoppedEvent,
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  dashboardClientAPIAtom,
  dashboardColumnFilterModelAtom,
  dashboardColumnStateAtom,
  selectedRowsAtom,
} from './atoms';
import { ServerDashboardJob } from './dashboardType';

export const useGridHandlers = () => {
  const gridApiReference = useRef<GridApi | null>(null);
  const setSelectedRows = useSetAtom(selectedRowsAtom);
  const setDashboardClientAPI = useSetAtom(dashboardClientAPIAtom);
  const savedColumnFilterModel = useAtomValue(dashboardColumnFilterModelAtom);
  const setColumnFilterModel = useSetAtom(dashboardColumnFilterModelAtom);
  const savedColumnState = useAtomValue(dashboardColumnStateAtom);
  const setColumnState = useSetAtom(dashboardColumnStateAtom);

  const onGridReady = useCallback(
    (parameters: GridReadyEvent) => {
      gridApiReference.current = parameters.api;
      setDashboardClientAPI(parameters.api);

      // Restore AG Grid's column filter model so header filter icons and
      // filter panels reflect the persisted filter state.
      if (savedColumnFilterModel) {
        parameters.api.setFilterModel(savedColumnFilterModel);
      }

      // Restore column visibility/order/width/pinning as previously chosen
      // in the sidebar's Columns tool panel.
      if (savedColumnState) {
        parameters.api.applyColumnState({
          applyOrder: true,
          state: savedColumnState,
        });
      }
    },
    [savedColumnFilterModel, savedColumnState, setDashboardClientAPI]
  );

  const persistColumnState = useCallback(
    (api: GridApi) => {
      setColumnState(api.getColumnState());
    },
    [setColumnState]
  );

  const onColumnVisible = useCallback(
    (event: ColumnVisibleEvent) => {
      const isColumnNowHidden = !event.visible;
      if (isColumnNowHidden && event.columns) {
        for (const column of event.columns) {
          event.api.setColumnFilterModel(column.getColId(), null);
        }
        event.api.onFilterChanged();
      }

      persistColumnState(event.api);
    },
    [persistColumnState]
  );

  const onColumnPinned = useCallback(
    (event: ColumnPinnedEvent) => {
      persistColumnState(event.api);
    },
    [persistColumnState]
  );

  // Covers the end of both column-move and column-resize drags in one place,
  // so we persist the settled state once instead of on every intermediate
  // drag/resize tick.
  const onDragStopped = useCallback(
    (event: DragStoppedEvent) => {
      persistColumnState(event.api);
    },
    [persistColumnState]
  );

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      const selectedNodes = event.api.getSelectedRows() as ServerDashboardJob[];
      setSelectedRows(selectedNodes);
    },
    [setSelectedRows]
  );

  const onFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      setColumnFilterModel(event.api.getFilterModel());
    },
    [setColumnFilterModel]
  );

  return {
    gridApiReference,
    onColumnPinned,
    onColumnVisible,
    onDragStopped,
    onFilterChanged,
    onGridReady,
    onSelectionChanged,
  };
};



